import "./CreateBookingPopup.scss";
import MemberIdTag from "../../../components/UI/MemberIdTag/MemberIdTag";
import { useCallback, useEffect, useRef, useState } from "react";
import Popup from "../../../hoc/Popup/Popup";
import {
  IActivityObject,
  IBooking,
  ICancelBookingInputType,
  ICoach,
  IConfirmBookingSpec,
  ICoPlayerBooking,
  ICourt,
  ICourtType,
  IIndividualWithStatus,
  IInitiateBookingResponse,
  IPermission,
  IPrices,
  ISummaryItem,
  IUser,
} from "../../../types/api";
import { Id, toast } from "react-toastify";
import {
  freeOnJuniorDropIn,
  inDropInWindow,
  isJuniorType,
} from "../../../shared/memberType";
import { CognitoUser } from "../../../types/cognito";
import { capitalizeWords, formatDateToSwedish } from "../../../shared/utility";
import {
  confirmBooking,
  expireBooking,
  getActiveBookingsByMember,
} from "../../../api/bookings/bookings";
import time from "../../../assets/images/time.svg";
import greenCheck from "../../../assets/images/green-check.svg";
import useBeforeUnload from "../../../shared/hooks/useBeforeUnload";
import singleCross from "../../../assets/images/single-corss.svg";
import { StatusEnum } from "../../../shared/enums";
import { searchUsers } from "../../../api/users/users";
import Spinner from "../../../components/UI/Spinner/Spinner";
import { getSlotByMember } from "../../../api/summaries/summaries";
import { getActiveBookingsAsCoplayerQueryFn } from "../../../api/bookings/bookingsQueries";
import { combinedQuery } from "../../../api/combinedQueries/combinedQueries";
import HandleConfirmationPopup from "../../HandleConfirmation/HandleConfirmationPopup";

interface ICreateBookingPopupProps {
  showPopup: boolean;
  onClose: () => void;
  onConfirm: () => void;
  court: ICourt;
  filterDate: Date;
  courtType: string;
  user: CognitoUser | null;
  timeAndCourtString: string;
  bookingToConfirm?: IInitiateBookingResponse;
  coachesList: string[] | [];
  chosenCoach?: ICoach;
  latestCoPlayers: IUser[] | [];
  coaches: ICoach[];
  courts: ICourt[];
  adminChosenUser?: IUser;
  price?: string;
  permissions: IPermission[];
  prices: IPrices[];
  isAdmin: boolean;
}
interface IAdditiveOption {
  value: "COACH" | "CO-PLAYER" | "HITTING_PARTNER";
}
function CreateBookingPopup(props: ICreateBookingPopupProps) {
  const [userString, setUserString] = useState<string>("");
  const [disableConfirmBtn, setDisableConfirmBtn] = useState<boolean>(false);
  const [searchString, setSearchString] = useState<string>("");
  const [searchedUsers, setSearchedUsers] = useState<IUser[]>([]);
  const [isJunior, setIsJunior] = useState<boolean>(false);
  const [isJuniorWarning, setIsJuniorWarning] = useState<boolean>(false);
  const [isDropIn, setIsDropIn] = useState<boolean>(false);
  const [sendEmail, setSendEmail] = useState<boolean>(true);
  const [showJuniorCoachWarning, setShowJuniorCoachWarning] =
    useState<boolean>(false);

  const memberType =
    props.user?.signInUserSession.idToken.payload["cognito:groups"];

  useEffect(() => {
    if (props.adminChosenUser) {
      setUserString(
        `${capitalizeWords(props.adminChosenUser.first_name)} ${capitalizeWords(
          props.adminChosenUser.last_name,
        )}`,
      );
    } else {
      setUserString(props.user?.attributes?.name || "");
    }

    if (props.chosenCoach) {
      if (
        props.coachesList.some((coach) => coach === props.chosenCoach?.coach_id)
      ) {
        setChosenCoach(props.chosenCoach.coach_id);
      }
    } else {
      setChosenCoach("");
    }

    setIsJunior(false);
    setIsJuniorWarning(false);

    if (memberType && memberType[0] === "user") {
      const userPermission = props.permissions.find(
        (permission) =>
          permission.permission_id === props.user?.attributes["custom:role"],
      );
      if (
        userPermission &&
        (userPermission.member_type === "Junior" ||
          userPermission.member_type === "Junior B")
      ) {
        setIsJunior(true);
      }
    }
    if (props.isAdmin && props.adminChosenUser) {
      const userPermission = props.adminChosenUser?.member_type;

      if (
        userPermission &&
        (userPermission === "Junior" || userPermission === "Junior B")
      ) {
        setIsJuniorWarning(true);
      }
    }
  }, [props.showPopup]);

  // A booking reception makes in the drop-in window is a drop-in - see
  // inDropInWindow for when it opens per membership. The admin can still
  // switch it off.
  useEffect(() => {
    const start = props.bookingToConfirm?.datetime_start;
    if (
      props.showPopup &&
      props.isAdmin &&
      props.adminChosenUser &&
      start &&
      inDropInWindow(
        props.adminChosenUser.member_type,
        start,
        props.court?.court_type
      )
    ) {
      setIsDropIn(true);
    }
  }, [props.showPopup, props.bookingToConfirm?.datetime_start]);

  useEffect(() => {
    if (props.showPopup) {
      if (props.bookingToConfirm) {
        const bookingData = {
          booking_id: props.bookingToConfirm.booking_id || "",
          datetime_start: props.bookingToConfirm.datetime_start,
          court_id: props.bookingToConfirm.court_id,
          slot_index: props.bookingToConfirm.slot_index,
          coach_id: [],
        };
        localStorage.setItem("bookingToExpire", JSON.stringify(bookingData));
      }
    } else {
      const savedData = localStorage.getItem("bookingToExpire");
      if (savedData) {
        const bookingToExpire = JSON.parse(savedData);
        expireBooking(bookingToExpire)
          .then(() => {
            localStorage.removeItem("bookingToExpire");
            props.onConfirm();
            props.onClose();
          })
          .catch((error) => {
            console.error("Failed to expire booking:", error);
            localStorage.removeItem("bookingToExpire");
          });
      }
    }

    return () => {
      localStorage.removeItem("bookingToExpire");
    };
  }, [props.showPopup, props.bookingToConfirm]);

  useBeforeUnload(
    "Din bokning kommer att avbrytas om du lämnar sidan.",
    props.showPopup,
    () => handleClose,
  );

  const { coachesList, latestCoPlayers, coaches } = props;
  const [chosenCoPlayers, setChosenCoPlayers] = useState<IUser[]>([]);
  const [chosenCoach, setChosenCoach] = useState<string>("");
  const [availableCoPlayers, setAvailableCoPlayers] = useState<IUser[]>([]);
  const [guests, setGuests] = useState<string[]>([]);
  const [totalCoPlayers, setTotalCoPlayers] = useState<number>(0);
  const [totalPeopleInBooking, setTotalPeopleInBooking] = useState<number>(0);
  const { weekday, dateAndMonth } = formatDateToSwedish(props.filterDate);
  const [currentAdditiveChoice, setCurrentAdditiveChoice] =
    useState<IAdditiveOption>({ value: "CO-PLAYER" });
  const [currentAdditiveText, setCurrentAdditiveText] =
    useState<string>("Välj medspelare");
  const additiveOptions = [
    { label: "Medspelare", value: "CO-PLAYER", text: "Välj medspelare" },
    { label: "Tränare", value: "COACH", text: "Välj tränare" },
    {
      label: "Hitting Partner",
      value: "HITTING_PARTNER",
      text: "Välj Hitting Partner",
    },
  ];
  const [countDown, setCountDown] = useState<number>(300);
  const clockColorClass = countDown < 60 ? "color-red" : "";
  const [coPlayerSlots, setCoPlayerSlots] = useState<{
    [key: string]: ISummaryItem;
  }>({});

  const [guestSlot, setGuestSlot] = useState<ISummaryItem | null>(null);

  const [previousChosenCoPlayers, setPreviousChosenCoPlayers] = useState<
    IUser[]
  >([]);

  const handleConfirm = () => {
    localStorage.removeItem("bookingToExpire");
    setChosenCoPlayers([]);
    setGuests([]);
    setChosenCoach("");
    setCurrentAdditiveChoice({ value: "CO-PLAYER" });
    setCurrentAdditiveText("Välj medspelare");
    setIsDropIn(false);
    props.onConfirm();
    setCountDown(300);
    props.onClose();
  };

  useEffect(() => {
    setTotalCoPlayers(chosenCoPlayers.length + guests.length);
    setTotalPeopleInBooking(
      chosenCoPlayers.length + guests.length + (chosenCoach !== "" ? 1 : 0),
    );
  }, [guests, chosenCoPlayers, chosenCoach]);

  const handleCoPlayerSlot = async (
    datetime_start: string,
    member_id: string,
    court_id: string,
    court_type: ICourtType,
    co_player_type?: string,
  ) => {
    try {
      // A junior's drop-in is priced by its own rules (see freeOnJuniorDropIn),
      // so it asks for the ordinary price and applies the free cases here.
      const juniorDropIn = isDropIn && isJuniorType(props.adminChosenUser?.member_type);
      const response = await getSlotByMember(
        datetime_start,
        court_id,
        court_type,
        member_id,
        isDropIn && !juniorDropIn,
      );
      if (
        juniorDropIn &&
        response &&
        !("message" in response) &&
        freeOnJuniorDropIn(co_player_type, datetime_start)
      ) {
        return { ...response, price: 0 };
      }
      return response;
    } catch (error: any) {
      toast.error(error);
    }
  };

  const handleGuestSlot = async (
    datetime_start: string,
    court_id: string,
    court_type: ICourtType,
  ) => {
    try {
      const response = await getSlotByMember(
        datetime_start,
        court_id,
        court_type,
        undefined,
        true,
      );
      return response;
    } catch (error: any) {
      toast.error(error);
    }
  };
  const fetchCoPlayerData = async () => {
    const promises = chosenCoPlayers.map((player) =>
      handleCoPlayerSlot(
        props.bookingToConfirm?.datetime_start || "",
        player.member_id,
        props.bookingToConfirm?.court_id || "",
        props.court.court_type as ICourtType,
        player.member_type,
      ).then((response) => ({ [player.member_id]: response })),
    );
    Promise.all(promises).then((responses) => {
      const slots = responses.reduce((acc, curr) => ({ ...acc, ...curr }), {});
      setCoPlayerSlots((prev) => ({ ...prev, ...slots }));
    });
    setPreviousChosenCoPlayers(chosenCoPlayers);
  };

  const fetchActiveBookingsByMember = async (member_id: string) => {
    if (member_id === "9215" || member_id === "3783") return;
    try {
      const response = await combinedQuery({
        active: true,
        requestor: "admin",
        member_id: member_id,
        fetchActiveBookingsByMemberId: true,
        fetchActiveBookingsCoPlayer: true,
        fetchActiveActivities: true,
      });
      let activeBookings: IBooking[] = [];
      let activeCoPlayerBookings: ICoPlayerBooking[] = [];
      let activeActivities: IActivityObject[] = [];
      if (response.activeBookings && !("message" in response.activeBookings)) {
        activeBookings = response.activeBookings;
      }
      if (
        response.activeBookingsCoPlayer &&
        !("message" in response.activeBookingsCoPlayer)
      ) {
        activeCoPlayerBookings = response.activeBookingsCoPlayer;
      }
      if (
        response.activeActivities &&
        !("message" in response.activeActivities)
      ) {
        activeActivities = response.activeActivities;
      }
      const chosenUser = chosenCoPlayers.find(
        (user) => user.member_id === member_id,
      );

      if (!chosenUser) {
        return;
      }
      const isUserAvailable = checkUserBookings(
        chosenUser,
        props.bookingToConfirm!,
        props.court,
        activeBookings,
        activeCoPlayerBookings,
        activeActivities,
        props.courts,
      );
      if (!isUserAvailable) {
        chosenCoPlayers.pop();
      }
    } catch (error: any) {
      toast.error(error);
    }
  };

  const checkUserBookings = (
    chosenUser: IUser,
    bookingToConfirm: IInitiateBookingResponse,
    court: ICourt,
    activeBookings: IBooking[],
    activeBookingsAsCoPlayer: ICoPlayerBooking[],
    activeActivitiesForUser: IActivityObject[],
    courts: ICourt[],
  ): boolean => {
    const slotStart = new Date(bookingToConfirm.datetime_start);
    const slotMinus30 = new Date(slotStart.getTime() - 30 * 60000);
    const slotPlus30 = new Date(slotStart.getTime() + 30 * 60000);

    const warn = (msg: string) => {
      toast.warning(msg);
      return false;
    };

    const overlappingActivity = activeActivitiesForUser.find((activity) => {
      const actStart = new Date(activity.activity.datetime_start);
      return actStart >= slotMinus30 && actStart <= slotPlus30;
    });
    if (overlappingActivity) {
      return warn(
        `${capitalizeWords(
          chosenUser.first_name,
        )} har redan en bokad aktivitet vid ${
          overlappingActivity.activity.datetime_start
        }.`,
      );
    }

    const conflict = activeBookings.find((b) => {
      const bookingStart = new Date(b.datetime_start);
      return (
        b.court_id !== court.court_id &&
        bookingStart >= slotMinus30 &&
        bookingStart <= slotPlus30
      );
    });
    if (conflict) {
      const courtName =
        courts.find((c) => c.court_id === conflict.court_id)?.name ||
        conflict.court_id;
      return warn(
        `${capitalizeWords(
          chosenUser.first_name,
        )} har redan en bokning vid ${conflict.datetime_start.slice(
          11,
          16,
        )} på bana ${courtName}.`,
      );
    }

    const coPlayerConflict = activeBookingsAsCoPlayer.find((b) => {
      const bookingStart = new Date(b.date);
      return (
        b.court_id !== court.court_id &&
        bookingStart >= slotMinus30 &&
        bookingStart <= slotPlus30
      );
    });
    if (coPlayerConflict) {
      const courtName =
        courts.find((c) => c.court_id === coPlayerConflict.court_id)?.name ||
        coPlayerConflict.court_id;
      return warn(
        `${capitalizeWords(
          chosenUser.first_name,
        )} är medspelare på en bokning vid ${coPlayerConflict.date.slice(
          11,
          16,
        )} på bana ${courtName}.`,
      );
    }

    return true;
  };

  useEffect(() => {
    if (
      chosenCoPlayers.length > 0 &&
      previousChosenCoPlayers !== chosenCoPlayers
    ) {
      fetchCoPlayerData();
    }

    if (props.isAdmin && chosenCoPlayers.length) {
      fetchActiveBookingsByMember(
        chosenCoPlayers[chosenCoPlayers.length - 1].member_id,
      );
    }
  }, [chosenCoPlayers]);

  useEffect(() => {
    fetchCoPlayerData();
  }, [isDropIn]);

  useEffect(() => {
    if (guests.length > 0 && guests.length <= 1) {
      handleGuestSlot(
        props.bookingToConfirm?.datetime_start || "",
        props.bookingToConfirm?.court_id || "",
        props.court.court_type as ICourtType,
      ).then((response) => {
        setGuestSlot(response);
      });
    }
  }, [guests]);

  useEffect(() => {
    setAvailableCoPlayers(latestCoPlayers);
  }, [latestCoPlayers, props.showPopup]);

  const handleClose = async () => {
    if (disableConfirmBtn) return;
    setCurrentAdditiveChoice({ value: "CO-PLAYER" });

    if (props.bookingToConfirm === undefined) return;
    const bookingToExpire: ICancelBookingInputType = {
      booking_id: props.bookingToConfirm.booking_id,
      datetime_start: props.bookingToConfirm.datetime_start,
      court_id: props.bookingToConfirm.court_id,
      slot_index: props.bookingToConfirm.slot_index,
      coach_id: [],
    };

    try {
      const response = await expireBooking(bookingToExpire);
      if (response) {
        handleConfirm();
        return;
      }
      toast.error("Något gick fel, försök igen");
    } catch (error: any) {
      toast.error(error);
    } finally {
      setGuestSlot(null);
      setIsDropIn(false);
    }
  };

  useEffect(() => {
    let interval: any = null;
    const initialCountDown = 300; // Initial countdown time in seconds (5 minutes)

    // Function to update the countdown based on the actual elapsed time
    const tick = () => {
      const savedStartTime = Number(localStorage.getItem("countdownStartTime")); // Get the saved start time
      const elapsedTime = Math.floor((Date.now() - savedStartTime) / 1000); // Calculate elapsed time in seconds
      const remainingTime = Math.max(0, initialCountDown - elapsedTime); // Calculate the remaining time

      setCountDown(remainingTime); // Update the countdown state

      if (remainingTime <= 0) {
        clearInterval(interval);
        toast.error(
          "Din bokning avbröts för att inte ha slutförts inom 5 minuter.",
        );
        handleClose();
        localStorage.removeItem("countdownStartTime"); // Clean up saved start time
      } else if (remainingTime === 60) {
        toast.warning("Vänligen avsluta din bokning inom 1 min");
      }
    };

    if (props.showPopup) {
      // Set the start time only if it's not already set
      if (!localStorage.getItem("countdownStartTime")) {
        localStorage.setItem("countdownStartTime", Date.now().toString());
      }
      interval = setInterval(tick, 1000); // Update the countdown every second
    } else {
      // Cleanup when the popup is closed
      localStorage.removeItem("countdownStartTime");
    }

    return () => {
      clearInterval(interval); // Clear the interval when the component unmounts
    };
  }, [props.showPopup]);

  useEffect(() => {
    if (countDown === 0) {
      handleClose();
    }
  }, [countDown]);

  const formatTime = () => {
    const minutes = Math.floor(countDown / 60);
    const seconds = countDown % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };
  const handleAdditiveChoiceToggle = (value: IAdditiveOption) => {
    setCurrentAdditiveChoice(value);
    const selectedOption = additiveOptions.find(
      (option) => option.value === value.value,
    );
    if (selectedOption) {
      setCurrentAdditiveText(selectedOption.text);
    }
  };

  const handleConfirmBooking = async () => {
    if (props.bookingToConfirm === undefined) return;
    if (totalPeopleInBooking < 1 && !isDropIn) {
      toast.error("Du måste lägga till minst en medspelare eller gäst");
      return;
    }

    setDisableConfirmBtn(true);
    const co_players: IIndividualWithStatus[] = chosenCoPlayers.map(
      (player) => ({
        member_id: player.member_id,
        status: StatusEnum.PENDING,
      }),
    );

    const coach = chosenCoach !== "" ? [chosenCoach] : [];
    const guestsList = guests.map((guest) => ({
      member_id: guest,
      status: StatusEnum.PENDING,
    }));

    const bookingToConfirm: IConfirmBookingSpec = {
      booking_id: props.bookingToConfirm.booking_id,
      booking_type: props.bookingToConfirm.booking_type,
      court_id: props.bookingToConfirm.court_id,
      guests: guestsList,
      member_id: props.bookingToConfirm.member_id,
      member_status: props.bookingToConfirm.member_status,
      member_name: props.bookingToConfirm.member_name,
      slot_index: props.bookingToConfirm.slot_index,
      status: props.bookingToConfirm.status,
      datetime_start: props.bookingToConfirm.datetime_start,
      co_players: co_players,
      coach_id: coach,
      created_at: props.bookingToConfirm.created_at,
      created_by: props.bookingToConfirm.created_by,
    };
    const memberType =
      props.user?.signInUserSession.idToken.payload["cognito:groups"];

    if (memberType === undefined) {
      toast.error("Något gick fel, försök igen");
      setDisableConfirmBtn(false);
      return;
    }
    if (isJunior) {
      if (chosenCoach === "") {
        toast.error("Du måste välja en tränare");
        setDisableConfirmBtn(false);
        return;
      }
    }
    let bookingToast = "" as Id;

    try {
      bookingToast = toast.loading("Bokar tid...");
      const bookingConfirmResponse = await confirmBooking(
        bookingToConfirm,
        memberType[0],
        sendEmail,
        isDropIn,
      );
      if (bookingConfirmResponse && !("message" in bookingConfirmResponse)) {
        toast.success("Bokning bekräftad!");
        toast.dismiss(bookingToast);
        handleConfirm();
      } else {
        toast.dismiss(bookingToast);
      }
      setDisableConfirmBtn(false);
    } catch (error: any) {
      setDisableConfirmBtn(false);
      toast.dismiss(bookingToast);
      toast.error(error);
    }
  };

  const addCoach = (coach: string) => {
    setChosenCoach(coach);
    const isHittingPartner = coaches.find(
      (c) => c.coach_id === coach,
    )?.is_hitting_partner;
    toast.success(isHittingPartner ? "Hitting Partner vald" : "Tränare vald");
  };
  const removeCoach = (coach: string) => () => {
    setChosenCoach("");
    toast.success("Tränare borttagen");
  };
  const addPlayerAsCoPlayer = (player: IUser, source: string) => {
    if (isForbiddenCoPlayer(player)) {
      toast.error("Den här spelaren kan inte väljas");
      return;
    }
    if (
      (player.member_type === "Vilande spelrätt" ||
        player.member_type === "Vilande spelrätt..") &&
      chosenCoach !== ""
    ) {
      toast.warning(
        "Vilande spelrätt har inte behörighet att boka med tränare",
      );
      return;
    }
    setSearchString("");
    setSearchedUsers([]);
    if (totalCoPlayers === 3) {
      toast.error("Du kan inte välja fler än 3 medspelare");
      return;
    }

    setChosenCoPlayers((prev) => [...prev, { ...player, source }]);

    toast.success(
      `Medspelare ${
        capitalizeWords(player.first_name) +
        " " +
        capitalizeWords(player.last_name)
      } tillagd`,
    );
  };

  const addGuest = () => {
    setGuests((prev) => [...prev, "guest"]);
  };

  const removeCoPlayer = (player: any) => () => {
    setChosenCoPlayers((prev) =>
      prev.filter((p) => p.member_id !== player.member_id),
    );

    toast.success(
      `Medspelare ${
        capitalizeWords(player.first_name) +
        " " +
        capitalizeWords(player.last_name)
      } borttagen`,
    );
  };

  const lastWarningTimeRef = useRef<number>(0);

  const showWarning = useCallback(() => {
    const now = Date.now();
    if (now - lastWarningTimeRef.current >= 5000) {
      lastWarningTimeRef.current = now;
      toast.warning("Inga användare hittades");
    }
  }, []);

  const latestUserSearchRef = useRef<string>("");

  const fetchSearchUsers = async (search: string) => {
    latestUserSearchRef.current = search;
    let requestor = props.adminChosenUser
      ? "admin"
      : ("user" as "admin" | "user");
    try {
      const response = await searchUsers(
        search,
        requestor,
        requestor === "admin",
        "CO_PLAYER",
      );
      if (latestUserSearchRef.current !== search) {
        return;
      }
      if (response.length === 0) {
        showWarning();
      }
      setSearchedUsers(response);
    } catch (error: any) {
      toast.error(error);
    }
  };

  useEffect(() => {
    if (searchString.length > 0) {
      handleSearchUsers();
    } else {
      setSearchedUsers([]);
    }
  }, [searchString]);

  const handleSearchUsers = () => {
    fetchSearchUsers(searchString);
  };

  const isForbiddenCoPlayer = (player: IUser) => {
   
    return (
      (memberType?.[0] === "user" &&
        (player.member_id === "9215" || player.member_id === "3783")) ||
      player.member_type === "Före detta medlem"
    );
  };

  useEffect(() => {
    setAvailableCoPlayers(
      latestCoPlayers.filter((p) => !isForbiddenCoPlayer(p)),
    );
  }, [latestCoPlayers, props.showPopup]);

  /** End search users  */

  const isReady = props.bookingToConfirm !== undefined;
  const chosenCoachData = chosenCoach
    ? coaches.find((c) => c.coach_id === chosenCoach)
    : null;

  // --- Hitting Partner tab logic ---
  const chosenCoachIsHittingPartner = !!chosenCoachData?.is_hitting_partner;
  const hasRegularCoachChosen = chosenCoach !== "" && !chosenCoachIsHittingPartner;
  const hittingPartnersAvailable = coachesList.some(
    (id) => coaches.find((c) => c.coach_id === id)?.is_hitting_partner,
  );
  // The Hitting Partner tab is exclusive: only offered when one is available and
  // no co-player, regular coach or guest has been added to the booking.
  const canShowHittingPartnerTab =
    hittingPartnersAvailable &&
    chosenCoPlayers.length === 0 &&
    guests.length === 0 &&
    !hasRegularCoachChosen;

  return (
    <Popup
      showPopup={props.showPopup}
      onClose={handleClose}
      leftTopElement={<span className="text-m">Din bokning</span>}
      loading={!isReady}
    >
      <div className="general-popup-wrapper">
        <form
          className="general-popup-form less-gap"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <div className="general-popup-form-top min-height-35px">
            {isReady && (
              <span className="text-m clock-wrapper">
                <img src={time} alt="clock"></img>
                <span className={clockColorClass}>{formatTime()}</span>
              </span>
            )}
            {isReady ? (
              <button
                className="text-m"
                onClick={() => {
                  if (!disableConfirmBtn) {
                    if (isJuniorWarning && chosenCoach === "" && !isDropIn) {
                      setShowJuniorCoachWarning(true);
                      return;
                    }
                    handleConfirmBooking();
                  }
                }}
              >
                Boka tid
              </button>
            ) : (
              <div className="spinner-wrapper">
                <Spinner />
              </div>
            )}
          </div>
          <div className="general-popup-form-group mb-20">
            <div className="general-popup-form-field">
              <div className="general-popup-flex-between">
                <span className="text-m primary-text">Datum</span>
                <div className="text-m primary-text text-svg-wrapper">
                  <span>
                    {weekday}, {dateAndMonth}
                  </span>
                  <img src={greenCheck} alt="green check"></img>
                </div>
              </div>
              <div className="general-popup-flex-between">
                <span className="text-m primary-text ">Tid + Bana</span>
                <div className="text-m primary-text text-svg-wrapper">
                  <span>{props.timeAndCourtString}</span>
                  <img src={greenCheck} alt="green check"></img>
                </div>
              </div>
              <div className="general-popup-flex-between">
                <span className="text-m primary-text">Huvudbokare</span>
                <div className="text-m primary-text text-svg-wrapper">
                  <span>
                    {userString}{" "}
                    <MemberIdTag
                      memberId={props.bookingToConfirm?.member_id}
                      // Only set when an admin books on someone's behalf; a
                      // member booking for themselves has no IUser here, so
                      // the tag falls back to a plain id.
                      memberType={props.adminChosenUser?.member_type}
                    />
                  </span>
                  <img src={greenCheck} alt="green check"></img>
                </div>
              </div>
              <div className="general-popup-flex-between align-top">
                <span className="text-m primary-text">Medspelare</span>
                <span className="text-m primary-text text-svg-wrapper">
                  <div className="chosen-players">
                    {chosenCoPlayers.map((player, index) => {
                      const coPlayerPermission = props.permissions.find(
                        (permission) =>
                          permission.member_type === player.member_type,
                      );

                      let coPlayerPrice = 0;
                      const coPlayerSlot = coPlayerSlots[player.member_id];

                      const coPlayerPriceString =
                        coPlayerSlot !== undefined ? coPlayerSlot.price : 0;
                      coPlayerPrice = Number(coPlayerPriceString);
                      let isForetag = false;
                      if (
                        (coPlayerPermission &&
                          coPlayerPermission.member_type === "Företag") ||
                        (coPlayerPermission &&
                          coPlayerPermission.member_type === "Företag B")
                      ) {
                        isForetag = true;
                      }
                      if (props.bookingToConfirm) {
                        const start = new Date(
                          props.bookingToConfirm.datetime_start,
                        );

                        // weekday: 1–5 are Mon–Fri
                        const weekday = start.getDay(); // 0=Sun, 6=Sat
                        const isWeekday = weekday >= 1 && weekday <= 5;

                        // extract time
                        const hour = start.getHours();

                        // 16:00–18:00 check
                        const isOutside16and18 = hour < 16 || hour > 18;

                        // special rule check
                        const isSpecial5300Weekday =
                          props.bookingToConfirm.court_id === "5305" &&
                          chosenCoach.length > 0 &&
                          isWeekday &&
                          isOutside16and18;
                        const isSpecial5300Weekend =
                          props.bookingToConfirm.court_id === "5305" &&
                          chosenCoach.length > 0 &&
                          !isWeekday;
                        if (isSpecial5300Weekend) {
                          coPlayerPrice = 0;
                        }
                        if (isSpecial5300Weekday) {
                          coPlayerPrice = 0;
                        }
                      }
                      return (
                        <div key={index} className="chosen-player">
                          {capitalizeWords(player.first_name)}{" "}
                          {capitalizeWords(player.last_name)}{" "}
                          <MemberIdTag
                            memberId={player.member_id}
                            memberType={player.member_type}
                          />{" "}
                          {Number(coPlayerPrice)} kr
                          {isForetag && <div>(pris kan tillkomma)</div>}
                          <img
                            onClick={removeCoPlayer(player)}
                            src={singleCross}
                            alt="cross"
                            className="cross-img pointer"
                          ></img>
                        </div>
                      );
                    })}
                    {guests.map((guest, index) => {
                      // let price = props.price;
                      // price = props.price;

                      // if (props.courtType === "OUTDOOR") {
                      //   price =
                      //     props.prices
                      //       .find((priceType) => priceType.color === "red")
                      //       ?.price.toString() || "";
                      // }
                      let price = 0;
                      if (guestSlot) {
                        price = Number(guestSlot.price);
                      }
                      return (
                        <div key={index} className="chosen-player">
                          Gäst {index + 1} - {price} kr
                          <img
                            onClick={() =>
                              setGuests((prev) =>
                                prev.filter((g, i) => i !== index),
                              )
                            }
                            src={singleCross}
                            alt="cross"
                            className="cross-img pointer"
                          ></img>
                        </div>
                      );
                    })}
                  </div>
                </span>
              </div>
              {coachesList.length > 0 && (
                <div className="general-popup-flex-between">
                  <span className="text-m primary-text">
                    {chosenCoachIsHittingPartner ? "Hitting Partner" : "Tränare"}
                  </span>
                  <span className="text-m primary-text text-svg-wrapper">
                    {chosenCoachData && (
                      <div className="added-coach-remove-wrapper">
                        <span>
                          {capitalizeWords(chosenCoachData.first_name)}{" "}
                          {capitalizeWords(chosenCoachData.last_name)} (
                          {/* Bollmaksin har inte extra pris */}
                          {chosenCoachData.coach_id === "5513"
                            ? `${chosenCoachData.coach_price} kr`
                            : `${
                                chosenCoachData.coach_price +
                                chosenCoPlayers.length * 50
                              } kr`}
                          )
                        </span>
                        <img
                          onClick={removeCoach(chosenCoach)}
                          src={singleCross}
                          alt="cross"
                          className=" cross-img pointer"
                        ></img>
                      </div>
                    )}
                  </span>
                </div>
              )}
              {props.isAdmin && (
                <>
                  <div className="general-popup-flex-between">
                    <span className="text-m secondary-text">Drop in</span>
                    <label className="toggle text-m">
                      <input
                        type="checkbox"
                        name="closed"
                        checked={isDropIn}
                        onChange={(e) => setIsDropIn(!isDropIn)}
                      />
                      <span className="slider round"></span>
                    </label>
                  </div>
                  <div className="general-popup-flex-between">
                    <span className="text-m secondary-text">Skicka Email</span>
                    <label className="toggle text-m">
                      <input
                        type="checkbox"
                        name="closed"
                        checked={sendEmail}
                        onChange={(e) => setSendEmail(!sendEmail)}
                      />
                      <span className="slider round"></span>
                    </label>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="general-popup-lila-border-group">
            <div className="co-player-trainer-wrapper">
              <div className="user-booking-court-type">
                <div className="user-booking-text-toggle-wrapper">
                  <div className="user-bookings-toggle-text text-l">
                    {currentAdditiveText}
                  </div>
                  <div className="users-bookings-toggle-buttons">
                    {additiveOptions.map((option) => {
                      const trainersAvailable = coachesList.length > 0;
                      const bookingHasVilandeSpelratt = chosenCoPlayers.some(
                        (coPlayer) =>
                          coPlayer.member_type === "Vilande spelrätt" ||
                          coPlayer.member_type === "Vilande spelrätt..",
                      );

                      if (option.value === "HITTING_PARTNER") {
                        // Only offered when a hitting partner is available and the
                        // booking has no co-player / regular coach / guest.
                        if (!canShowHittingPartnerTab) return null;
                      } else {
                        if (
                          !trainersAvailable ||
                          guests.length > 0 ||
                          bookingHasVilandeSpelratt
                        )
                          return null;
                        // Once a hitting partner is chosen the booking is
                        // exclusive — hide the other tabs.
                        if (chosenCoachIsHittingPartner) return null;
                      }

                      return (
                        <div
                          key={option.value}
                          className="user-booking-additive-toggle-wrapper"
                        >
                          <button
                            className={`toggle-button text-m ${
                              currentAdditiveChoice.value === option.value
                                ? "active"
                                : ""
                            }`}
                            onClick={() =>
                              handleAdditiveChoiceToggle(
                                option as IAdditiveOption,
                              )
                            }
                          >
                            {option.label}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {(currentAdditiveChoice.value === "COACH" ||
                  currentAdditiveChoice.value === "HITTING_PARTNER") &&
                  coachesList.length > 0 &&
                  guests.length === 0 && (
                    <div className="coach-choices-wrapper text-m">
                      <div>
                        <span>
                          {currentAdditiveChoice.value === "HITTING_PARTNER"
                            ? "Tillgängliga Hitting Partners"
                            : "Tillgängliga tränare"}
                        </span>
                      </div>
                      {coachesList.map((coach, index) => {
                        const coachWidthData = coaches.find(
                          (c) => c.coach_id === coach,
                        );
                        if (!coachWidthData) return;
                        const isHittingPartner =
                          !!coachWidthData.is_hitting_partner;
                        // Regular coaches show under Tränare, hitting partners
                        // under the Hitting Partner tab.
                        if (
                          currentAdditiveChoice.value === "HITTING_PARTNER" &&
                          !isHittingPartner
                        )
                          return null;
                        if (
                          currentAdditiveChoice.value === "COACH" &&
                          isHittingPartner
                        )
                          return null;
                        return (
                          <div
                            key={index}
                            className="coach-wrapper pointer"
                            onClick={() => addCoach(coach)}
                          >
                            <div className="coach-name">
                              {capitalizeWords(coachWidthData.first_name)}{" "}
                              {capitalizeWords(coachWidthData.last_name)}
                            </div>
                            <div className="coach-price-id-wrapper">
                              <div className="coach-id">({coach})</div>
                              <div className="coach-price">
                                {coachWidthData.coach_price} kr
                              </div>
                            </div>
                            <a className="choose-coach">
                              {isHittingPartner
                                ? " Välj Hitting Partner"
                                : " Välj Coach"}
                            </a>
                          </div>
                        );
                      })}
                    </div>
                  )}
                {currentAdditiveChoice.value === "CO-PLAYER" && (
                  <div className="co-player-choices-wrapper text-m">
                    <div>
                      <span>Senaste medspelare</span>
                    </div>
                    {availableCoPlayers.length > 0 ? (
                      availableCoPlayers.map((player, index) => {
                        if (
                          chosenCoPlayers.some(
                            (coPlayer) =>
                              coPlayer.member_id === player.member_id,
                          ) ||
                          player.member_id === props.bookingToConfirm?.member_id
                        ) {
                          return null;
                        }
                        if (player.member_type === "Före detta medlem")
                          return null;
                        if (index > 4) return null;
                        const isFrozen = player.frozen && props.adminChosenUser;
                        return (
                          <div
                            key={index}
                            className="player-wrapper pointer"
                            onClick={() =>
                              addPlayerAsCoPlayer(player, "available")
                            }
                          >
                            <div
                              className={`player-name ${
                                isFrozen ? "frozen" : ""
                              }`}
                            >
                              {capitalizeWords(player.first_name)}{" "}
                              {capitalizeWords(player.last_name)}
                            </div>
                            <div className="player-id">
                              {" "}
                              {player.member_id}{" "}
                            </div>
                            <a className="choose-player"> Välj medspelare</a>
                          </div>
                        );
                      })
                    ) : (
                      <div className="no-latest-players secondary-text">
                        Du har inte spelat med någon tidigare
                      </div>
                    )}
                    <div>
                      <span>Övriga medspelare</span>
                    </div>
                    <div className="search-input-btn-wrapper">
                      <div className="search-input-wrapper">
                        <input
                          className="users-search-input text-m"
                          value={searchString}
                          onChange={(e) => setSearchString(e.target.value)}
                          placeholder="Sök på namn & medlemsnummer"
                        ></input>
                        <i className="fa-regular fa-magnifying-glass search-icon"></i>
                      </div>
                    </div>
                    {searchedUsers.length > 0 &&
                      searchedUsers
                        .filter(
                          (user) =>
                            !(
                              memberType &&
                              memberType[0] === "user" &&
                              (user.member_id === "9215" ||
                                user.member_id === "3783")
                            ) &&
                            !availableCoPlayers.some(
                              (coPlayer) =>
                                coPlayer.member_id === user.member_id,
                            ) &&
                            !chosenCoPlayers.some(
                              (coPlayer) =>
                                coPlayer.member_id === user.member_id,
                            ) &&
                            user.member_id !==
                              props.bookingToConfirm?.member_id,
                        )
                        .map((player, index) => {
                          // if (player.member_type === "Före detta medlem")
                          //   return null;
                          if (index > 4) return null;

                          const isFrozen = player.frozen;

                          return (
                            <div
                              key={player.member_id}
                              className="player-wrapper pointer"
                              onClick={() =>
                                addPlayerAsCoPlayer(player, "filtered")
                              }
                            >
                              <div
                                className={`player-name ${
                                  isFrozen ? "frozen" : ""
                                }`}
                              >
                                <span>
                                  {capitalizeWords(player.first_name)}{" "}
                                  {capitalizeWords(player.last_name)}
                                </span>
                              </div>
                              <div className="player-id">
                                {player.member_id}
                              </div>
                              <a className="choose-player">Välj medspelare</a>
                            </div>
                          );
                        })}

                    <div className="add-guest-wrapper flex-row">
                      <button
                        onClick={() => addGuest()}
                        disabled={
                          totalCoPlayers === 3 || chosenCoach !== "" || isJunior
                        }
                      >
                        Lägg till gäst
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>
      <HandleConfirmationPopup
        showPopup={showJuniorCoachWarning}
        onClose={() => setShowJuniorCoachWarning(false)}
        onConfirm={() => {
          setShowJuniorCoachWarning(false);
          handleConfirmBooking();
        }}
        questionText="Junioren bokas utan tränare och utan drop in. Vill du fortsätta?"
        confirmText="Boka"
      />
    </Popup>
  );
}

export default CreateBookingPopup;
